import { useState, useRef } from "react";
import "./style.sass";
import { UploadModal } from "../upload-modal";
import { DescriptiveProcessing } from "../descriptive-processing";
import { AnswerEvaluationModal } from "../answer-evaluation";
import { useNotify } from "../../hooks/use-notify";
import { useApi } from "../../hooks/use-api";
import {
  API_DESCRIPTIVE_EVALUATE_UPSC,
  API_EVALUATE_DESCRIPTIVE_TYPO,
} from "../../utils/api-routes";
import { useSingleton } from "../../hooks/use-singleton";

export const QuestionSection = ({
  questionCount,
  questions = [],
  onReset,
  language,
  onSubmitAnswerSheet,
  questionPayload,
}) => {
  const [inputMode, setInputMode] = useState("upload");
  const [showUpload, setShowUpload] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [result, setResult] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [typedAnswers, setTypedAnswers] = useState({});
  const [activeRecording, setActiveRecording] = useState(null);
  const { mail, section, user } = useSingleton();
  const activeSection = section ?? user?.data?.section ?? null;

  const { toggleNotify } = useNotify();
  const { post } = useApi();
  const recognitionRefs = useRef({});

  const chapterName = questions?.[0]?.CHAPTER || "";
  const marks = questions?.[0]?.MARKS || "";
  const firstItem = questions?.[0] || {};

  const startRecording = (index) => {
    const SpeechRecognition =
      window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) return;
    if (recognitionRefs.current[index]) recognitionRefs.current[index].stop();
    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;

    // Map language name to BCP-47 code
    const langMap = {
      English: "en-US",
      Tamil: "ta-IN",
      Hindi: "hi-IN",
      Telugu: "te-IN",
      Kannada: "kn-IN",
      Malayalam: "ml-IN",
      Marathi: "mr-IN",
      Bengali: "bn-IN",
      Gujarati: "gu-IN",
      Punjabi: "pa-IN",
      Urdu: "ur-IN",
    };

    const selectedLang = language || firstItem.MEDIUM || "English";
    recognition.lang = langMap[selectedLang] || "en-US";

    recognition.onresult = (event) => {
      let transcript = "";
      for (let i = 0; i < event.results.length; i++)
        transcript += event.results[i][0].transcript;
      setTypedAnswers((prev) => ({ ...prev, [index]: transcript }));
    };
    recognition.onerror = () => setActiveRecording(null);
    recognition.onend = () => setActiveRecording(null);
    recognitionRefs.current[index] = recognition;
    recognition.start();
    setActiveRecording(index);
  };

  const stopRecording = (index) => {
    if (recognitionRefs.current[index]) recognitionRefs.current[index].stop();
    setActiveRecording(null);
  };

  const toggleMic = (index) => {
    if (activeRecording === index) stopRecording(index);
    else {
      if (activeRecording !== null) stopRecording(activeRecording);
      startRecording(index);
    }
  };

  const handleTypedSubmit = async () => {
    if (activeRecording !== null) stopRecording(activeRecording);
    setIsSubmitting(true);
    setIsProcessing(true);
    const answersPayload = questions.map((q, i) => ({
      QUESTION: q.QUESTION,
      ANSWER: typedAnswers[i] || "",
    }));

    if (onSubmitAnswerSheet) {
      try {
        await onSubmitAnswerSheet(typedAnswers);
      } catch (e) {
        console.log(e);
      } finally {
        setIsSubmitting(false);
        setIsProcessing(false);
      }
      return;
    }
    const payload = {
      mail: firstItem.MAIL || "",
      board: firstItem.BOARD || "",
      clas: firstItem.CLASS || "",
      medium: firstItem.MEDIUM || "English",
      subject: firstItem.SUBJECT || "",
      chapter: firstItem.CHAPTER || "",
      marks: firstItem.MARKS || 0,
      answers: answersPayload,
      section: activeSection,
    };
    try {
      const response = await post(API_EVALUATE_DESCRIPTIVE_TYPO, payload);
      setResult(response.data);
      setIsProcessing(false);
    } catch (error) {
      toggleNotify({
        type: "error",
        title: "Evaluation Failed",
        description: "Retry.",
      });
      setIsProcessing(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isProcessing) return <DescriptiveProcessing />;
  if (result)
    return (
      <AnswerEvaluationModal
        onClose={() => {
          setResult(null);
          onReset();
        }}
        data={result}
      />
    );

  const handleEvaluate = async (file) => {
    setShowUpload(false);
    setIsProcessing(true);

    const formData = new FormData();

    formData.append("mail", mail);
    formData.append("exam", questionPayload.exam);
    formData.append("medium", questionPayload.medium);
    formData.append("subject", questionPayload.subject);
    formData.append("year", questionPayload.year);
    formData.append("topics", questionPayload.topics);
    formData.append("marks", questionPayload.marks);
    formData.append("questions", JSON.stringify(questions));
    formData.append("file", file);

    const response = await post(API_DESCRIPTIVE_EVALUATE_UPSC, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });

    setResult(response.data);
    setIsProcessing(false);
  };

  return (
    <>
      <section className="question_card_details">
        <section className="chemical-card">
          <section className="card-header">
            <section className="title-section">
              <section className="icon-box">📄</section>
              <section>
                <h2>{chapterName}</h2>
                <p className="subtitle">
                  Complete revision material for quick exam preparation
                </p>
              </section>
            </section>
            <section className="marks-badge">{marks} Marks</section>
          </section>
          <section className="study-tip">
            <section className="tip-icon">💡</section>
            <section>
              <h4>Study Tip</h4>
              <p>
                Take your time to structure your answer properly. Include an
                introduction, main body with detailed explanations, and
                conclusion.
              </p>
            </section>
          </section>
          <section className="info-row">
            <span>📘 Est. Reading Time: 15 minutes</span>
            <span>⭐ Questions: {questionCount}</span>
          </section>
        </section>

        <section className="qs-mode-toggle-wrapper">
          {/* <button
            className={`qs-mode-btn ${inputMode === "type" ? "active" : ""}`}
            onClick={() => setInputMode("type")}
          >
            ⌨️ Type Answer
          </button> */}
          <button
            className={`qs-mode-btn ${inputMode === "upload" ? "active" : ""}`}
            onClick={() => setInputMode("upload")}
          >
            📤 Upload Sheet
          </button>
        </section>

        <section className="questions">
          {questions?.map((item, index) => {
            const { question } = item || {};
            const isRecording = activeRecording === index;
            return (
              <section key={index} className="question-item-full">
                <p className="question-title-bold">
                  {index + 1}. {question}
                </p>
                {/* {inputMode === "type" && (
                  <section className="answer-section-full">
                    <div className="your-answer-label-row">
                      <span className="pencil-icon">📝</span>
                      <span className="your-answer-text">Your Answer</span>
                    </div>
                    <div className="input-with-mic-wrapper">
                      <textarea
                        className="full-answer-textarea"
                        placeholder="Start typing your answer here..."
                        value={typedAnswers[index] || ""}
                        onChange={(e) =>
                          setTypedAnswers((prev) => ({
                            ...prev,
                            [index]: e.target.value,
                          }))
                        }
                        rows={1}
                      />
                      <button
                        className={`mic-button-inner ${isRecording ? "recording" : ""}`}
                        onClick={() => toggleMic(index)}
                      >
                        <svg
                          viewBox="0 0 24 24"
                          fill="currentColor"
                          width="20"
                          height="20"
                        >
                          <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3z" />
                          <path d="M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z" />
                        </svg>
                      </button>
                    </div>
                  </section>
                )} */}
              </section>
            );
          })}
        </section>
      </section>

      <section className="card-footer">
        <button className="reset-btn" onClick={onReset}>
          Reset
        </button>
        {inputMode === "type" ? (
          <button
            className="upload-btn"
            onClick={handleTypedSubmit}
            disabled={isSubmitting}
          >
            Submit Answer Sheet →
          </button>
        ) : (
          <button className="upload-btn" onClick={() => setShowUpload(true)}>
            Upload Answer Sheet ⬆
          </button>
        )}
      </section>

      {showUpload && inputMode === "upload" && (
        <UploadModal
          onClose={() => setShowUpload(false)}
          onSubmit={handleEvaluate}
        />
      )}
    </>
  );
};
